import React, { useEffect, useContext, useState, Suspense } from 'react';
import { bool, node, string } from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { dataModel } from '../dataModel';
import OrderSampleContent from './OrderSampleContent';
import { useOrderSample } from '../hooks/useOrderSample';
import { publish } from '../analytics';
// eslint-disable-next-line max-len
const SampleDrawer = React.lazy(() => import(/* webpackChunkName: "order-sample-drawer" */ './SampleDrawer/SampleDrawer'));
const defaultState = {
  sampleDrawerRequested: false,
  gettingSampleData: false,
  sampleAvailability: 'Available'
};

export const OrderSample = ({
  children,
  itemId,
  link,
  oosMessage,
  showTitle
}) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('order-sample.ready'); }, []);
  const [orderSampleState, setOrderSampleState] = useState(defaultState);
  const {
    sampleDrawerRequested,
    gettingSampleData,
    sampleAvailability
  } = orderSampleState;
  const { channel } = useContext(ExperienceContext);

  const { storeId, storeZip: zipCode } = useStore();
  const { data, loading: dataLoading } = useOrderSample({ itemId, storeId, zipCode });

  const {
    dimensionText,
    discontinued,
    isSuperSku,
    parentId,
    price,
    sampleId,
    sampleProduct
  } = data;

  if (!sampleProduct || dataLoading || !sampleId || (!price && !discontinued)) {
    return null;
  }

  const checkSamplesAvailable = ({ loading, orderSamples }) => {
    let sampleStatus = 'Available';
    if (!loading) {
      const samplesAvailable = (orderSamples || []).length;
      const inStock = !!orderSamples?.find((sample) => sample.inStockStatus);
      const notDiscontinued = !!orderSamples?.find((sample) => !sample.discontinued);

      const allSamplesOutOfStock = !!(samplesAvailable && !inStock);
      const allSamplesDiscontinued = !!(samplesAvailable && !notDiscontinued);
      if (allSamplesDiscontinued) {
        sampleStatus = 'Discontinued';
      } else if (allSamplesOutOfStock) {
        sampleStatus = 'Out of Stock';
      }
    }

    setOrderSampleState((prevState) => {
      return {
        ...prevState,
        gettingSampleData: loading,
        sampleAvailability: sampleStatus
      };
    });
  };

  const handleClick = async () => {
    if (isSuperSku) {
      setOrderSampleState((prevState) => {
        return {
          ...prevState,
          gettingSampleData: true,
          sampleDrawerRequested: true,
          loadingSample: true,
        };
      });
    } else {
      const cartReqParams = {
        itemId: sampleId,
        quantity: 1,
        fulfillmentMethod: 'ShipToHome',
        fulfillmentLocation: zipCode
      };

      const cartOptions = {
        channel,
        host: window.location.origin.includes('localhost') ? 'https://www.homedepot.com' : window.location.origin,
        misship: false,
        paypal: false
      };

      window.LIFE_CYCLE_EVENT_BUS.trigger('cart.add-to-cart', {
        cartReqParams,
        cartOptions,
      });
    }
    publish('order-sample.click');
  };

  const sampleDrawerClose = () => {
    setOrderSampleState((prevState) => {
      return {
        ...prevState,
        sampleDrawerRequested: false,
      };
    });
  };
  const shouldDrawerOpen = (sampleDrawerRequested && !gettingSampleData && sampleAvailability === 'Available');

  return (
    <>
      <OrderSampleContent
        dimensionText={dimensionText}
        handleClick={handleClick}
        link={link}
        loading={gettingSampleData}
        multipleSamples={isSuperSku}
        oosMessage={oosMessage}
        price={price}
        sampleAvailability={sampleAvailability}
        showTitle={showTitle}
      >
        {children}
      </OrderSampleContent>
      {sampleDrawerRequested
        && (
          <Suspense fallback={<div />}>
            <SampleDrawer
              anchorSampleId={sampleId}
              channel={channel}
              dimensionText={dimensionText}
              onClose={sampleDrawerClose}
              onSampleData={checkSamplesAvailable}
              open={shouldDrawerOpen}
              parentId={parentId}
              storeId={storeId}
              zipCode={zipCode}
            />
          </Suspense>
        )}
    </>
  );
};

OrderSample.displayName = 'OrderSample';

OrderSample.dataModel = dataModel;

OrderSample.propTypes = {
  children: node,
  itemId: string.isRequired,
  link: bool,
  oosMessage: bool,
  showTitle: bool
};

OrderSample.defaultProps = {
  children: 'Need a closer look?',
  link: false,
  oosMessage: false,
  showTitle: true
};
